/**
 * GRN lookup dialog shown on entry to the NCR create screen. Entering a GRN
 * number auto-populates supplier, PO, part, and pricing details.
 * T3 (QUALITY-NCR-REVIEW, 30 Sep 2026): one way to use the number. The
 * magnifier beside the box did exactly what the footer button did, which read
 * as a search; the button now says what it does.
 * CR-8717: the box now also searches — typing part of a GRN number lists the
 * matches, and picking one fills the box with its full number for "Use this GRN".
 * Extracted from NCRCreate for the 500-line file limit (CR-6387).
 */
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Loader2, FileText, SkipForward } from 'lucide-react';
import type { GoodsReceiptNote } from '../../../lib/api';
import { cn } from '../../../lib/utils';

/** CR-8717: fewer characters than this match most of the tenant's GRNs, so no search runs. */
export const GRN_SEARCH_MIN_CHARS = 2;

interface NCRGrnLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  lookingUp: boolean;
  onLookup: () => void;
  onSkip: () => void;
  /** CR-8717: GRNs whose number contains what has been typed. */
  results?: GoodsReceiptNote[];
  /** CR-8717: a search for the typed text is running. */
  searching?: boolean;
}

/** The detail line under a result: part, supplier and PO, whichever are known. */
function resultDetail(grn: GoodsReceiptNote): string {
  return [grn.part_number, grn.supplier_name, grn.po_number && `PO ${grn.po_number}`].filter(Boolean).join(' · ');
}

export default function NCRGrnLinkDialog({
  open, onOpenChange, value, onValueChange, lookingUp, onLookup, onSkip,
  results = [], searching = false,
}: NCRGrnLinkDialogProps) {
  const term = value.trim();
  const showList = term.length >= GRN_SEARCH_MIN_CHARS;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Link a Goods Receipt Note</DialogTitle>
          <DialogDescription>
            Do you have a GRN number? Entering it will auto-populate supplier, PO, part, and pricing details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="grn_dialog_input">GRN Number</Label>
            <Input
              id="grn_dialog_input"
              className="mt-1"
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onLookup(); } }}
              placeholder="Search by GRN number, e.g. 620252"
              autoFocus
              autoComplete="off"
              aria-describedby="grn_dialog_hint"
            />
            <p id="grn_dialog_hint" className="text-xs text-muted-foreground mt-1">
              Type part of the number to search, or paste the full number.
            </p>
          </div>
          {showList && (
            <div className="max-h-60 overflow-y-auto rounded-md border" aria-live="polite">
              {searching ? (
                <p className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />Searching...
                </p>
              ) : results.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No GRNs match &quot;{term}&quot;.</p>
              ) : (
                <ul aria-label="Matching GRNs">
                  {results.map((grn) => {
                    const selected = grn.grn_number === term;
                    return (
                      <li key={grn.id}>
                        <button
                          type="button"
                          onClick={() => onValueChange(grn.grn_number)}
                          aria-pressed={selected}
                          className={cn(
                            'w-full text-left px-3 py-2 text-sm hover:bg-muted focus:bg-muted focus:outline-none',
                            selected && 'bg-primary/10',
                          )}
                        >
                          <span className="block font-medium">{grn.grn_number}</span>
                          <span className="block text-xs text-muted-foreground">{resultDetail(grn)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onSkip} className="w-full sm:w-auto">
            <SkipForward className="h-4 w-4 mr-2" />
            Skip - No GRN
          </Button>
          <Button onClick={onLookup} disabled={lookingUp || !value.trim()} className="w-full sm:w-auto">
            {lookingUp ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Finding the GRN...</>
            ) : (
              <><FileText className="h-4 w-4 mr-2" />Use this GRN</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
